import { Component, ElementRef, EventEmitter, HostBinding, OnInit, Output, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { Point, SVG, Svg } from '@svgdotjs/svg.js';
import _ from 'lodash';
import panzoom, { PanZoom } from 'panzoom';
import {
  Countertop,
  CountertopAngleProcessingBase,
  CountertopLine,
  CountertopLinesJoint,
  CountertopPanel,
  RenderOptions,
} from '../../models/cutting/countertop';
import { ClampJoint } from '../../models/user/cutting-settings.model';

export type CountertopSchemaComponentRenderMode = 'schema' | 'texture';

@Component({
  selector: 'csl-countertop-schema',
  templateUrl: 'countertop-schema.component.html',
  standalone: true,
  imports: [MatLegacyButtonModule, MatIconModule],
})
export class CountertopSchemaComponent implements OnInit {
  @HostBinding('style.--countertop-schema-scale') schemaScale = 1;

  private _countertop: Countertop;
  private _mode: CountertopSchemaComponentRenderMode;
  private _options: RenderOptions;
  private _lastSizeChangedPanelUID?: string;

  @ViewChild('schema', { static: true }) schema: ElementRef;

  draw: Svg;

  zoomInstance: PanZoom;

  @Output() panelSizeChange: EventEmitter<{
    countertop: Countertop;
    line: CountertopLine;
    panel: CountertopPanel;
    error?: string;
  }> = new EventEmitter();

  @Output() panelSplit: EventEmitter<{
    countertop: Countertop;
    line: CountertopLine;
    panel: CountertopPanel;
    error?: string;
  }> = new EventEmitter();

  @Output() panelDelete: EventEmitter<{
    countertop: Countertop;
    line: CountertopLine;
    panel: CountertopPanel;
    error?: string;
  }> = new EventEmitter();

  @Output() angleSelectorClick: EventEmitter<{ angleNumber: number; angleProcessing: CountertopAngleProcessingBase }> =
    new EventEmitter();

  @Output() sideSelectorClick: EventEmitter<{ sideNumber: number }> = new EventEmitter();
  @Output() linesJointSelectorClick: EventEmitter<{ countertop: Countertop; joint: CountertopLinesJoint }> =
    new EventEmitter();
  @Output() clampJointClick: EventEmitter<{ countertop: Countertop; joint: ClampJoint }> = new EventEmitter();

  constructor() {}

  ngOnInit(): void {
    this.draw = SVG().addTo(this.schema.nativeElement);

    this.zoomInstance = panzoom((this.schema.nativeElement as HTMLDivElement).getElementsByTagName('svg')[0], {
      filterKey: () => true,
      maxZoom: 10,
      minZoom: 0.1,
      initialX: 0,
      initialY: 0,
      initialZoom: 1,
      smoothScroll: false,
    });

    this.zoomInstance.on('transform', (e: PanZoom) => {
      this.schemaScale = +e.getTransform().scale;

      if (this._countertop && !_.isNil(this._mode)) {
        this.render(this._countertop, this._mode, this._options);
      }
    });
  }

  zoom(delta: number): void {
    const container = this.schema.nativeElement as HTMLDivElement;
    const rect = {
      width: container.clientWidth,
      height: container.clientHeight,
      x: container.clientLeft,
      y: container.clientTop,
    };
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    this.zoomInstance.smoothZoom(cx, cy, delta);
  }

  resetZoom(countertop?: Countertop): void {
    this.zoomInstance.moveTo(0, 0);
    this.zoomInstance.zoomAbs(0, 0, 1);

    if (!countertop) {
      return;
    }

    const maxDimension = Math.max(countertop.length, countertop.width);

    const clientWidth = (this.schema.nativeElement as HTMLDivElement).clientWidth;
    const clientHeight = (this.schema.nativeElement as HTMLDivElement).clientHeight;

    const padding = this.getSvgPadding(countertop);

    const minClientDimension = Math.min(clientWidth, clientHeight);

    const center = new Point(clientWidth / 2, clientHeight / 2);

    this.zoomInstance.moveTo(center.x - (padding + countertop.length / 2), center.y - (padding + countertop.width / 2));
    this.zoomInstance.zoomAbs(center.x, center.y, minClientDimension / maxDimension);
  }

  render(
    countertop: Countertop,
    mode: CountertopSchemaComponentRenderMode = 'schema',
    renderOptions?: RenderOptions,
    resetZoom?: boolean
  ): void {
    if (!countertop) {
      return;
    }

    if (this._countertop !== countertop || resetZoom) {
      this.resetZoom(countertop);
    }

    this._mode = mode;
    this._countertop = countertop;
    this._options = { ...renderOptions };

    this._options.panelSizes = this._options.panelSizes ?? this._lastSizeChangedPanelUID;

    this.reset(countertop);

    switch (mode) {
      case 'schema':
        countertop.render(
          this.draw,
          this.schemaScale,
          (...[countertop, line, panel, side, value, error]) => {
            this._lastSizeChangedPanelUID = panel.uid;
            this.panelSizeChange.emit({ countertop, line, panel, error });
          },
          (countertop, line, panel, mode) =>
            mode === 'add'
              ? this.panelSplit.emit({ countertop, line, panel })
              : this.panelDelete.emit({ countertop, line, panel }),
          (angleNumber, angleProcessing) => this.angleSelectorClick.emit({ angleNumber: angleNumber, angleProcessing }),
          sideNumber => this.sideSelectorClick.emit({ sideNumber }),
          joint => this.linesJointSelectorClick.emit({ countertop, joint }),
          joint => this.clampJointClick.emit({ countertop, joint }),
          this._options
        );

        this._lastSizeChangedPanelUID = this._options.panelSizes = undefined;
        break;

      case 'texture':
        countertop.renderTextureSchema(this.draw);
        break;
    }
  }

  reset(countertop: Countertop): void {
    this.draw.clear();

    const padding = this.getSvgPadding(countertop);

    this.draw.viewbox(-padding, -padding, countertop.length + padding * 2, countertop.width + padding * 2);
    this.draw.size(countertop.length + padding * 2, countertop.width + padding * 2);
  }

  resetZoomClickHandler(): void {
    this.resetZoom(this._countertop);
  }

  private getSvgPadding(countertop: Countertop): number {
    return Math.max(countertop.length, countertop.width);
  }
}
